import { BoxProps } from "@mui/material";
import { isNull } from "lodash";
import { useEffect, useMemo, useRef } from "react";

import { components } from "@/@types/api";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { useConfig } from "@/hooks/useConfig";
import { ToolBarShared } from "@/shared/components/DataGrid/ToolBarShared";
import { Toggle } from "@/shared/components/DataGrid/ToolBarShared";
import DataGridContainer from "@/shared/components/DataGridContainer";
import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";

import { getBreakdownTable } from "../../../utils/spend-revenue-summary";
import { useScenarioAnalyticsV3 } from "../../../utils/useScenarioAnalyticsV3";
import { useScenarioResultsStore } from "../store/useScenarioResultsStore";
import { getColumnDefinitions } from "./getColumnDefinitions";

type Revenue = components["schemas"]["ScenarioResultRevenue"];
type Spend = components["schemas"]["ScenarioResultSpend"];
type Scenario = components["schemas"]["NewScenarioV2"];

interface ScenarioBreakdownTableProps extends BoxProps {
  revenues: Revenue[];
  spends: Spend[];
  isGeoEnabled?: boolean;
  scenario?: Scenario;
  selectedGeography?: string | null;
}

const DEFAULT_COLUMNS = [
  "business_unit",
  "channel",
  "funnel_stage",
  "geography",
  "spend",
  "revenue",
  "roi",
  "volume",
  "roas",
];

const generateFileName = (
  scenarioName: string,
  isGeoEnabled: boolean,
  selectedGeography: string | null
): string => {
  if (isGeoEnabled) {
    const geography = selectedGeography || "National";
    return `${scenarioName}-${geography}`;
  }

  return `${scenarioName}`;
};

export const ScenarioBreakdownTable = ({
  revenues,
  spends,
  isGeoEnabled = false,
  scenario,
  selectedGeography,
}: ScenarioBreakdownTableProps) => {
  const { config } = useConfig();
  const { formatCurrency } = config.currency;

  const containerApiRef = useRef(null);

  // Initialize scenario analytics
  const { trackScenarioBreakdownTableDownloaded } = useScenarioAnalyticsV3({ scenario });

  const {
    geoView: geoViewToggle,
    businessUnitToggle,
    activeColumns,
    metricType,
  } = useScenarioResultsStore((state) => state.grid);

  const {
    setGeoView: setGeoViewToggle,
    setBusinessUnitToggle,
    setActiveColumns,
    setMetricType,
  } = useScenarioResultsStore((state) => state);

  const { mapBcpName } = useDefaultNodeNameInfo();

  const table = useMemo(() => {
    return getBreakdownTable(revenues, spends, businessUnitToggle, geoViewToggle);
  }, [revenues, spends, businessUnitToggle, geoViewToggle]);

  // Simulated units column should only be visible for new scenarios which will have non-null volume values (old scenario results will be populated with null)
  const isSimulatedUnitsEnabled = !revenues.every((revenue) => isNull(revenue.volume));

  // Reset metric type if simulated units are disabled and current metric is SALES_UNITS
  useEffect(() => {
    if (!isSimulatedUnitsEnabled && metricType === MetricType.SALES_UNITS) {
      setMetricType(MetricType.MARKETING_ROI);
    }
  }, [isSimulatedUnitsEnabled, metricType, setMetricType]);

  const columns = useMemo(() => {
    return getColumnDefinitions(activeColumns, {
      businessUnitToggle,
      geoViewToggle,
      formatCurrency,
      mapBcpName,
      isGeoEnabled,
      metricType,
    });
  }, [activeColumns, businessUnitToggle, geoViewToggle, formatCurrency, isGeoEnabled, metricType]);

  const fileName = useMemo(() => {
    return generateFileName(scenario?.name || "Scenario", isGeoEnabled, selectedGeography ?? null);
  }, [scenario?.name, isGeoEnabled, selectedGeography]);

  const toggles: Toggle[] = [
    {
      id: "businessUnitToggle",
      selectedValue: businessUnitToggle,
      options: [
        { label: "Aggregate Business Units", value: "aggregate" },
        { label: "Split by Business Unit", value: "split" },
      ],
    },
  ];

  // Add geo toggle only if geo is enabled
  if (isGeoEnabled) {
    toggles.push({
      id: "geoViewToggle",
      selectedValue: geoViewToggle,
      options: [
        { label: "Aggregate Geographies", value: "aggregate" },
        { label: "Split by Geography", value: "split" },
      ],
    });
  }

  const metricDropdownConfig = {
    label: "Show",
    value: metricType,
    options: [
      { label: "Marketing ROI (%)", value: MetricType.MARKETING_ROI },
      { label: "Return on Ad Spend (%)", value: MetricType.RETURN_ON_AD_SPEND },
    ],
    onChange: (value: string | null) => {
      if (value) {
        setMetricType(value as MetricType);
      }
    },
  };

  if (isSimulatedUnitsEnabled) {
    metricDropdownConfig.options.push({ label: "Sales Units", value: MetricType.SALES_UNITS });
  }

  return (
    <DataGridContainer
      apiRef={containerApiRef}
      rows={table}
      columns={columns}
      metricDropdown={metricDropdownConfig}
      toolbarComponent={
        <ToolBarShared
          apiRef={containerApiRef}
          columns={columns}
          activeColumns={activeColumns}
          toggles={toggles}
          tableName="Scenario Breakdown"
          fileName={fileName}
          onDownload={trackScenarioBreakdownTableDownloaded}
          handleApplyFilters={({
            activeColumns,
            toggles,
          }: {
            activeColumns: string[];
            toggles: { [id: string]: string };
          }) => {
            const businessUnitToggle = toggles["businessUnitToggle"];
            const geoViewToggleValue = toggles["geoViewToggle"];
            setActiveColumns(activeColumns);

            if (businessUnitToggle) {
              setBusinessUnitToggle(businessUnitToggle as BusinessUnitToggle);
            }

            if (geoViewToggleValue) {
              setGeoViewToggle(geoViewToggleValue as GeoViewToggle);
            }
          }}
          handleResetFilters={() => {
            setActiveColumns(DEFAULT_COLUMNS);
            setBusinessUnitToggle(BusinessUnitToggle.AGGREGATE);
            setGeoViewToggle(GeoViewToggle.AGGREGATE);
          }}
        />
      }
      options={{
        rowHeight: 60,
        sortModel: [{ field: "spend", sort: "desc" }],
      }}
    />
  );
};
